import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import entryService from "./entryService";
import { setAlertError, setAlertSuccess } from "../alert/alertSlice";

const initialState = {
  entries: [],
  entry: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
  isDeleting: false,
  message: "",
  validationResults: null,
  isValidating: false,
};

export const getEntries = createAsyncThunk(
  "entry/getEntries",
  async (_, thunkAPI) => {
    try {
      const response = await entryService.getEntries();
      // thunkAPI.dispatch(setAlertSuccess("Entries fetched successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to fetch entries";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getAllEntriesWithDetails = createAsyncThunk(
  "entry/getAllEntriesWithDetails",
  async (_, thunkAPI) => {
    try {
      const response = await entryService.getAllEntriesWithDetails();
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to fetch entries";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const getPlayerEntries = createAsyncThunk(
  "entry/getPlayerEntries",
  async (playerId, thunkAPI) => {
    try {
      const response = await entryService.getPlayerEntries(playerId);
      // thunkAPI.dispatch(setAlertSuccess("Player entries fetched successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to fetch player entries";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const addEntry = createAsyncThunk(
  "entry/addEntry",
  async (entryData, thunkAPI) => {
    try {
      const response = await entryService.addEntry(entryData);
      thunkAPI.dispatch(setAlertSuccess("Entry added successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to add entry";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const updateEntry = createAsyncThunk(
  "entry/updateEntry",
  async (entryData, thunkAPI) => {
    try {
      const response = await entryService.updateEntry(entryData.id, entryData);
      thunkAPI.dispatch(setAlertSuccess("Entry updated successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to update entry";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const deleteEntry = createAsyncThunk(
  "entry/deleteEntry",
  async (id, thunkAPI) => {
    try {
      const response = await entryService.deleteEntry(id);
      thunkAPI.dispatch(setAlertSuccess("Entry deleted successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to delete entry";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const setEntryValid = createAsyncThunk(
  "entry/setEntryValid",
  async (id, thunkAPI) => {
    try {
      const response = await entryService.setEntryValid(id);
      thunkAPI.dispatch(setAlertSuccess("Entry marked as valid"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to mark entry as valid";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const setEntryInvalid = createAsyncThunk(
  "entry/setEntryInvalid",
  async ({ id, reason, markedInvalidBy }, thunkAPI) => {
    try {
      const response = await entryService.setEntryInvalid(
        id,
        reason,
        markedInvalidBy,
      );
      thunkAPI.dispatch(setAlertSuccess("Entry marked as invalid"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to mark entry as invalid";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const validateEntriesFromCsv = createAsyncThunk(
  "entry/validateEntriesFromCsv",
  async (validationData, thunkAPI) => {
    try {
      const response =
        await entryService.validateEntriesFromCsv(validationData);
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to validate entries";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const importEntriesFromCsv = createAsyncThunk(
  "entry/importEntriesFromCsv",
  async (importData, thunkAPI) => {
    try {
      const response = await entryService.importEntriesFromCsv(importData);
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to import entries";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  },
);

export const entrySlice = createSlice({
  name: "entry",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEntries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.entries = action.payload.data.entries;
      })
      .addCase(getEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getAllEntriesWithDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllEntriesWithDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.entries =
          action.payload.data.entries || action.payload.entries || [];
      })
      .addCase(getAllEntriesWithDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(getPlayerEntries.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPlayerEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.entries = action.payload.data;
      })
      .addCase(getPlayerEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(addEntry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.entries.push(action.payload.data.entry);
      })
      .addCase(addEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(updateEntry.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const updatedEntry = action.payload.data.entry;
        const index = state.entries.findIndex(
          (entry) => entry.id === updatedEntry.id,
        );
        if (index !== -1) {
          state.entries[index] = updatedEntry;
        } else {
          console.warn("Entry not found in state for ID:", updatedEntry.id);
        }
      })
      .addCase(updateEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(deleteEntry.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteEntry.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.isSuccess = true;
        state.entries = state.entries.filter(
          (entry) => entry.id !== action.payload.data.entry.id,
        );
      })
      .addCase(deleteEntry.rejected, (state, action) => {
        state.isDeleting = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(setEntryValid.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setEntryValid.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const updatedEntry = action.payload.data.entry;

        const index = state.entries.findIndex(
          (entry) => entry.id === updatedEntry.id,
        );
        if (index !== -1) {
          state.entries[index] = updatedEntry;
        }
      })
      .addCase(setEntryValid.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(setEntryInvalid.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setEntryInvalid.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const updatedEntry = action.payload.data.entry;

        const index = state.entries.findIndex(
          (entry) => entry.id === updatedEntry.id,
        );
        if (index !== -1) {
          state.entries[index] = updatedEntry;
        }
      })
      .addCase(setEntryInvalid.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(importEntriesFromCsv.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importEntriesFromCsv.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(importEntriesFromCsv.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      .addCase(validateEntriesFromCsv.pending, (state) => {
        state.isValidating = true;
        state.validationResults = null;
      })
      .addCase(validateEntriesFromCsv.fulfilled, (state, action) => {
        state.isValidating = false;
        state.validationResults = action.payload.data;
      })
      .addCase(validateEntriesFromCsv.rejected, (state, action) => {
        state.isValidating = false;
        state.validationResults = null;
      });
  },
});

export default entrySlice.reducer;
